import type { RouterConfig } from "@nuxt/schema";
import { START_LOCATION } from "vue-router";

// Nuxt's default _getHashElementScrollMarginTop uses document.querySelector(),
// which throws a SyntaxError for hash IDs that begin with a digit (e.g. #1-purpose).
// The try/catch returns 0, so the scroll lands with no top offset and the sticky
// header obscures the heading. This override uses getElementById instead.
function getHashElementScrollMarginTop(selector: string): number {
    try {
        const elem = selector.startsWith("#")
            ? document.getElementById(selector.slice(1))
            : document.querySelector(selector);
        if (elem) {
            return (
                (Number.parseFloat(getComputedStyle(elem).scrollMarginTop) || 0) +
                (Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0)
            );
        }
    } catch {
        // ignore invalid selectors
    }
    return 0;
}

export default {
    scrollBehavior(to, from, savedPosition) {
        const nuxtApp = useNuxtApp();
        // scrollBehaviorType is a Nuxt-extended RouterOptions property not in vue-router's typings
        const hashScrollBehaviour =
            ((useRouter().options as unknown as Record<string, unknown>)?.scrollBehaviorType as ScrollBehavior) ??
            "auto";

        if (to.path.replace(/\/$/, "") === from.path.replace(/\/$/, "")) {
            if (from.hash && !to.hash) {
                return { left: 0, top: 0 };
            }
            if (to.hash) {
                return { el: to.hash, top: getHashElementScrollMarginTop(to.hash), behavior: hashScrollBehaviour };
            }
            return false;
        }

        const routeAllowsScrollToTop =
            typeof to.meta.scrollToTop === "function" ? to.meta.scrollToTop(to, from) : to.meta.scrollToTop;
        if (routeAllowsScrollToTop === false) {
            return false;
        }

        const hookToWait = nuxtApp._runningTransition ? "page:transition:finish" : "page:loading:end";
        return new Promise((resolve) => {
            if (from === START_LOCATION) {
                resolve(calculatePosition(to, from, savedPosition, hashScrollBehaviour));
                return;
            }
            nuxtApp.hooks.hookOnce(hookToWait, () => {
                requestAnimationFrame(() => resolve(calculatePosition(to, from, savedPosition, hashScrollBehaviour)));
            });
        });
    },
} satisfies RouterConfig;

function calculatePosition(
    to: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[0],
    from: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[1],
    savedPosition: Parameters<NonNullable<RouterConfig["scrollBehavior"]>>[2],
    defaultHashScrollBehaviour: ScrollBehavior
) {
    if (savedPosition) return savedPosition;

    if (to.hash) {
        return {
            el: to.hash,
            top: getHashElementScrollMarginTop(to.hash),
            behavior: defaultHashScrollBehaviour,
        };
    }

    return { left: 0, top: 0 };
}
